import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  buildCajasDisplayRows,
  buildCajasTableColumns,
  buildDesgloseDisplayRows,
  cajaRowCellValue,
  formatSoloTotalesDate,
  formatSoloTotalesMoney,
  RELACION_VENTAS_SOLO_TOTALES_TITULO,
  resolveSoloTotalesSecciones,
} from './relacionVentasSoloTotalesModel';
import { drawEncabezadoReportePdf } from './reporteEncabezadoPdf';
import { applyEncabezadoFiltrosUiFromFallback } from './reporteFiltrosEncabezadoExport';

const HEAD_BG = [15, 47, 89];
const HEAD_TEXT = [255, 255, 255];

function drawResumenBlock(doc, x, y, w, title, lines) {
  doc.setFillColor(245, 247, 250);
  doc.setDrawColor(203, 213, 225);
  doc.roundedRect(x, y, w, 8 + lines.length * 5.5, 2, 2, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(51, 65, 85);
  doc.text(title.toUpperCase(), x + 3, y + 5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(15, 23, 42);
  let ly = y + 10;
  for (const [label, value, opts = {}] of lines) {
    doc.text(String(label), x + 3, ly);
    doc.setFont('helvetica', opts.strong ? 'bold' : 'normal');
    doc.text(String(value), x + w - 3, ly, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    ly += 5.5;
  }
  return ly + 4;
}

/**
 * @param {object|null} resumen
 * @param {object} options
 */
export function downloadRelacionVentasSoloTotalesPdf(resumen, options = {}) {
  if (!resumen || typeof resumen !== 'object') return;

  const {
    fileBase = 'relacion-ventas-solo-totales',
    encabezado = null,
    encabezadoFallback = null,
    sucursalNombre = '',
    fechaInicio,
    fechaFin,
    cajaInicio,
    cajaFin,
    turnoInicio,
    turnoFin,
    ocultarFolios = false,
  } = options;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 14;
  let y = margin;

  const header = resumen.header ?? {};
  const filtros = resumen.filtros ?? {};
  const encBase = applyEncabezadoFiltrosUiFromFallback(
    {
      nombreComercial:
        encabezado?.nombreComercial ??
        encabezado?.empresaNombre ??
        header.empresaNombre ??
        encabezadoFallback?.nombreComercial ??
        '',
      domicilioFiscal:
        encabezado?.domicilioFiscal ??
        header.empresaDomicilio ??
        encabezadoFallback?.domicilioFiscal ??
        '',
      rfc: encabezado?.rfc ?? header.rfc ?? encabezadoFallback?.rfc ?? '',
      reporteTitulo: RELACION_VENTAS_SOLO_TOTALES_TITULO,
      periodoTexto:
        encabezado?.periodoTexto ??
        `Fechas: ${formatSoloTotalesDate(filtros.fechaInicio ?? fechaInicio)} — ${formatSoloTotalesDate(filtros.fechaFin ?? fechaFin)}`,
      cajasTexto:
        encabezado?.cajasTexto ??
        (filtros.cajaInicio != null || filtros.cajaFin != null || cajaInicio != null || cajaFin != null
          ? `${filtros.cajaInicio ?? cajaInicio ?? '—'} — ${filtros.cajaFin ?? cajaFin ?? filtros.cajaInicio ?? cajaInicio ?? '—'}`
          : encabezadoFallback?.cajasTexto ?? ''),
      turnosTexto:
        encabezado?.turnosTexto ??
        `${filtros.turnoInicio ?? turnoInicio ?? '—'} — ${filtros.turnoFin ?? turnoFin ?? filtros.turnoInicio ?? turnoInicio ?? '—'}`,
      sucursalNombre: header.sucursalNombre ?? encabezado?.sucursalNombre ?? sucursalNombre,
      sucursalesTexto: encabezadoFallback?.sucursalesTexto,
      sucursalesItems: encabezadoFallback?.sucursalesItems,
      cajasItems: encabezadoFallback?.cajasItems,
    },
    encabezadoFallback,
  );

  y = drawEncabezadoReportePdf(doc, encBase, pageW, margin, y, {
    fechaInicio: filtros.fechaInicio ?? fechaInicio,
    fechaFin: filtros.fechaFin ?? fechaFin,
    cajaInicio: filtros.cajaInicio ?? cajaInicio,
    cajaFin: filtros.cajaFin ?? cajaFin,
    turnoInicio: filtros.turnoInicio ?? turnoInicio,
    turnoFin: filtros.turnoFin ?? turnoFin,
  });

  const secciones = resolveSoloTotalesSecciones(resumen);
  const ocultarFoliosEfectivo =
    ocultarFolios === true ||
    resumen?.filtros?.ocultarFolios === true ||
    resumen?.alertas?.ocultarFolios === true;

  for (let i = 0; i < secciones.length; i += 1) {
    const seccion = secciones[i];
    if (resumen.agrupadoPorTurno) {
      if (y > 250) {
        doc.addPage();
        y = margin;
      }
      doc.setFillColor(15, 47, 89);
      doc.rect(margin, y, pageW - margin * 2, 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      const titulo =
        seccion.titulo ||
        (seccion.turnoNombre
          ? `Turno: ${seccion.turnoNombre}`
          : seccion.turno != null
            ? `Turno: ${seccion.turno}`
            : 'Turno: —');
      doc.text(String(titulo), pageW / 2, y + 5.5, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      y += 12;
    }

    y = renderSoloTotalesSeccionPdf(doc, seccion, { pageW, margin, y, ocultarFolios: ocultarFoliosEfectivo });
  }

  doc.save(`${fileBase}.pdf`);
}

function renderSoloTotalesSeccionPdf(doc, seccion, ctx) {
  let { pageW, margin, y, ocultarFolios = false } = ctx;

  const alertas = seccion.alertas ?? {};
  if (!ocultarFolios && alertas.faltanCheques && alertas.singleDateTurn) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(180, 83, 9);
    doc.text('Faltan cheques — verifique folios', pageW / 2, y, { align: 'center' });
    y += 6;
  }
  if (alertas.hayPendientes) {
    doc.setTextColor(185, 28, 28);
    doc.text('Hay cuentas sin saldar (se toman como efectivo)', pageW / 2, y, { align: 'center' });
    y += 6;
  }
  doc.setTextColor(0, 0, 0);

  const cajasRows = buildCajasDisplayRows(seccion.cajas);
  const cajasColumns = buildCajasTableColumns(ocultarFolios);
  if (cajasRows.length) {
    if (y > 250) {
      doc.addPage();
      y = margin;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(15, 23, 42);
    doc.text('RESUMEN POR CAJA', margin, y);
    y += 5;

    autoTable(doc, {
      startY: y,
      head: [cajasColumns.map((col) => col.label)],
      body: cajasRows.map((c) => cajasColumns.map((col) => String(cajaRowCellValue(c, col.id)))),
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: HEAD_BG, textColor: HEAD_TEXT, fontStyle: 'bold' },
      columnStyles: Object.fromEntries(
        cajasColumns.map((col, index) => [
          index,
          col.align === 'num' ? { halign: 'right' } : {},
        ]),
      ),
    });
    y = (doc.lastAutoTable?.finalY ?? y) + 6;
    doc.setTextColor(0, 0, 0);
  }

  const venta = seccion.venta ?? {};
  const cortesia = seccion.cortesia ?? {};
  const innerW = pageW - margin * 2;

  y = drawResumenBlock(doc, margin, y, innerW, 'Venta', [
    ['Subtotal', formatSoloTotalesMoney(venta.subtotal)],
    ['Descuento', formatSoloTotalesMoney(-Math.abs(Number(venta.descuento) || 0))],
    ...(Number(venta.monedero) > 0 ? [['Monedero', formatSoloTotalesMoney(-Math.abs(Number(venta.monedero)))] ] : []),
    ['Total', formatSoloTotalesMoney(venta.total), { strong: true }],
  ]);

  y = drawResumenBlock(doc, margin, y, innerW, 'Cortesía', [
    ['Subtotal', formatSoloTotalesMoney(cortesia.subtotal)],
    ...(Number(cortesia.descuento) > 0 ? [['Descuento', formatSoloTotalesMoney(cortesia.descuento)]] : []),
    ['Total', formatSoloTotalesMoney(cortesia.total), { strong: true }],
  ]);

  const renderDesglosePdf = (titulo, grupo) => {
    const { filas, totales } = buildDesgloseDisplayRows(grupo);
    if (!filas.length) return;
    if (y > 240) {
      doc.addPage();
      y = margin;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(titulo.toUpperCase(), margin, y);
    y += 4;
    autoTable(doc, {
      startY: y,
      head: [['Forma de pago', 'Pago', 'Propina', 'Total']],
      body: [
        ...filas.map((f) => [f.nombre, formatSoloTotalesMoney(f.pago), formatSoloTotalesMoney(f.propina), formatSoloTotalesMoney(f.total)]),
        ...(totales
          ? [
              ['Total', formatSoloTotalesMoney(totales.pago), formatSoloTotalesMoney(totales.propina), formatSoloTotalesMoney(totales.total)],
              ['Com. propina', '', '', formatSoloTotalesMoney(totales.comPropina)],
              ['Prop. s/com', '', '', formatSoloTotalesMoney(totales.propSinCom)],
            ]
          : []),
      ],
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: HEAD_BG, textColor: HEAD_TEXT, fontStyle: 'bold' },
      columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
    });
    y = (doc.lastAutoTable?.finalY ?? y) + 6;
  };

  renderDesglosePdf('Desglose de pagos — venta', seccion.desgloseVenta);
  renderDesglosePdf('Desglose de pagos — cortesía', seccion.desgloseCortesia);

  const abo = seccion.aliBebOtr ?? {};
  if (y > 230) {
    doc.addPage();
    y = margin;
  }
  autoTable(doc, {
    startY: y,
    head: [['Concepto', 'Venta', 'Cheque prom.']],
    body: [
      ['Alimentos', formatSoloTotalesMoney(abo.totalAli), formatSoloTotalesMoney(abo.chAli)],
      ['Bebidas', formatSoloTotalesMoney(abo.totalBeb), formatSoloTotalesMoney(abo.chBeb)],
      ['Otros', formatSoloTotalesMoney(abo.totalOtr), formatSoloTotalesMoney(abo.chOtr)],
      [`Total (${abo.totalPersonas ?? 0} personas)`, formatSoloTotalesMoney(abo.total), formatSoloTotalesMoney(abo.chProm)],
    ],
    margin: { left: margin, right: margin },
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: HEAD_BG, textColor: HEAD_TEXT, fontStyle: 'bold' },
    columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' } },
  });
  y = (doc.lastAutoTable?.finalY ?? y) + 8;

  const ef = seccion.efectivoFinal ?? {};
  const efectivoLines = [
    ['Efectivo', formatSoloTotalesMoney(ef.efectivo)],
    ['Propinas firmadas', formatSoloTotalesMoney(-Math.abs(Number(ef.propinasFirmadas) || 0))],
    ...(Array.isArray(ef.gastos) ? ef.gastos.map((g) => [String(g.concepto ?? 'Gasto'), formatSoloTotalesMoney(-Math.abs(Number(g.importe) || 0))]) : []),
    ['Efectivo final', formatSoloTotalesMoney(ef.total), { strong: true }],
  ];

  if (y > 220) {
    doc.addPage();
    y = margin;
  }

  doc.setDrawColor(15, 47, 89);
  doc.setLineWidth(0.6);
  doc.roundedRect(margin, y, pageW - margin * 2, 10 + efectivoLines.length * 6, 3, 3);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('EFECTIVO FINAL', pageW / 2, y + 6, { align: 'center' });
  let ey = y + 12;
  doc.setFontSize(9);
  for (const [label, value, opts = {}] of efectivoLines) {
    doc.setFont('helvetica', opts.strong ? 'bold' : 'normal');
    doc.text(String(label), margin + 4, ey);
    doc.text(String(value), pageW - margin - 4, ey, { align: 'right' });
    ey += 6;
  }

  return ey + 8;
}
